/** @jsxImportSource @emotion/react */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, Form, Spinner } from 'react-bootstrap'
import moment from 'moment'
import Select from 'react-select'

import DataTable from 'components/atoms/DataTable'
import { TableWrapper } from 'components/atoms/TableWrapper'
import {
  useExportBankDepositReportMutation,
  useExportBankDepositReportPdfMutation,
  useGetBankDepositReportQuery,
} from 'store/services/managementReports'
import { useLazyGetLabPatientListQuery } from 'store/services/patient'
import { useGetLabPayersQuery } from 'store/services/payer'
import { filterPayerOption, formatPayerOptionLabel } from 'components/lab/payer/PayerOptionLabel'
import { skipToken } from '@reduxjs/toolkit/query'
import { useDebounce } from 'use-debounce'
import { fullName } from 'helpers/nameHelper'
import { usePlatformMode } from 'hooks/usePlatformMode'
import {
  BankDepositPaymentMethod,
  BankDepositReportItem,
} from 'ts-back-end/src/reporting/types/bank-deposit-report'

type Option<T extends string> = {
  label: string
  value: T
  matchedName?: string | null
}

type AppliedFilters = {
  paymentDateFrom?: string
  paymentDateTo?: string
  paymentMethods?: BankDepositPaymentMethod[]
  patientId?: number
  payerId?: number
  billingLocation?: string
}

const PAGE_SIZE_DEFAULT = 20

const methodOptions: Option<BankDepositPaymentMethod>[] = [
  { label: 'Cash', value: 'CASH' },
  { label: 'Check', value: 'CHECK' },
  { label: 'Credit Card', value: 'CREDIT_CARD' },
  { label: 'ACH', value: 'ACH' },
  { label: 'Money Order', value: 'MONEY_ORDER' },
  { label: 'Care Credit', value: 'CARE_CREDIT' },
  { label: 'Other', value: 'OTHER' },
]

const methodLabelMap: Record<string, string> = methodOptions.reduce(
  (map, option) => ({ ...map, [option.value]: option.label }),
  {},
)

// What kind of money the line is. Mirrors BANK_DEPOSIT_SOURCE_LABELS on the
// backend, which the exports print from.
const sourceLabelMap: Record<string, string> = {
  PAYMENT: 'Insurance Payment',
  PREPAYMENT: 'Prepayment',
  STATEMENT: 'Statement Payment',
}

// Sleep Solutions, LLC is retired, so it is not offered as a filter.
const billingLocationOptions: Option<string>[] = [
  { label: 'TestSmarter, Inc.', value: 'TEST_SMARTER' },
  { label: 'TestSmarter DBA Sleep Solutions of North Florida', value: 'SLEEP_SOLUTIONS_NORTH_FL' },
  { label: 'iRhythm Technologies, Inc.', value: 'IRHYTHM' },
]

const billingLocationLabelMap: Record<string, string> = {
  TEST_SMARTER: 'TestSmarter, Inc.',
  // Retired, but legacy charges still carry it.
  SLEEP_SOLUTIONS: 'TestSmarter, Inc.',
  SLEEP_SOLUTIONS_NORTH_FL: 'TestSmarter DBA Sleep Solutions of North Florida',
  IRHYTHM: 'iRhythm Technologies, Inc.',
}

const formatDate = (value?: string | null) => (value ? moment(value).format('MM/DD/YYYY') : '—')

// The API sends an instant, so the time reads in the viewer's own zone.
const formatDateTime = (value?: string | null) =>
  value ? moment(value).format('MM/DD/YYYY hh:mm A') : '—'

const formatMoney = (value?: number | null) =>
  `$${Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export const BankDepositReport = () => {
  const { isIRhythmMode } = usePlatformMode()
  const [paymentDateFrom, setPaymentDateFrom] = useState('')
  const [paymentDateTo, setPaymentDateTo] = useState('')
  const [selectedMethods, setSelectedMethods] = useState<Option<BankDepositPaymentMethod>[]>([])
  const [selectedLocation, setSelectedLocation] = useState<Option<string> | null>(null)
  const [selectedPatient, setSelectedPatient] = useState<Option<string> | null>(null)
  const [selectedPayer, setSelectedPayer] = useState<Option<string> | null>(null)
  const [patientSearch, setPatientSearch] = useState('')
  const [payerSearch, setPayerSearch] = useState('')
  const [patientOptions, setPatientOptions] = useState<Option<string>[]>([])
  const [patientOptionsLoading, setPatientOptionsLoading] = useState(false)
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({})
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT)

  const [debouncedPatientSearch] = useDebounce(patientSearch, 300)
  const [debouncedPayerSearch] = useDebounce(payerSearch, 300)

  const { data, isFetching } = useGetBankDepositReportQuery({
    page: currentPage,
    size: pageSize,
    ...appliedFilters,
  })
  const [exportBankDepositReport, { isLoading: isExportingCsv }] = useExportBankDepositReportMutation()
  const [exportBankDepositReportPdf, { isLoading: isExportingPdf }] = useExportBankDepositReportPdfMutation()
  const [getPatients] = useLazyGetLabPatientListQuery()

  const { data: payerList, isFetching: payersLoading } = useGetLabPayersQuery(
    debouncedPayerSearch.trim().length >= 2 ? { active: true, name: debouncedPayerSearch.trim() } : skipToken,
  )

  const payerOptions = useMemo(() => {
    const options = (payerList || []).map((payer) => ({
      value: String(payer.payerId),
      label: payer.name,
      matchedName: payer.matchedName,
    }))

    // Keeps the current selection in the list once the search that found it is
    // no longer the one in the box.
    if (selectedPayer && !options.some((option) => option.value === selectedPayer.value)) {
      return [selectedPayer, ...options]
    }

    return options
  }, [payerList, selectedPayer])

  // Looks up by id when the term is a number, by name otherwise.
  useEffect(() => {
    const trimmed = debouncedPatientSearch.trim()

    if (!trimmed) {
      setPatientOptions(selectedPatient ? [selectedPatient] : [])
      setPatientOptionsLoading(false)
      return
    }

    let isActive = true

    const loadPatients = async () => {
      setPatientOptionsLoading(true)

      try {
        const normalized = trimmed.replace(/^#/, '').trim()
        const results = new Map<string, Option<string>>()

        const pushPatients = (
          items: Array<{ id: number | string; firstName?: string; lastName?: string }>,
        ) => {
          items.forEach((patient) => {
            results.set(String(patient.id), {
              value: String(patient.id),
              label: `#${patient.id} ${fullName(patient.firstName, patient.lastName)}`.trim(),
            })
          })
        }

        if (/^\d+$/.test(normalized)) {
          const response = await getPatients({ id: normalized, size: 10 }).unwrap()
          pushPatients(response.items || [])
        } else if (normalized.includes(' ')) {
          const [firstName, ...rest] = normalized.split(/\s+/)
          const response = await getPatients({ firstName, lastName: rest.join(' '), size: 10 }).unwrap()
          pushPatients(response.items || [])
        } else {
          const [byFirstName, byLastName] = await Promise.all([
            getPatients({ firstName: normalized, size: 10 }).unwrap(),
            getPatients({ lastName: normalized, size: 10 }).unwrap(),
          ])

          pushPatients(byFirstName.items || [])
          pushPatients(byLastName.items || [])
        }

        if (isActive) {
          setPatientOptions(Array.from(results.values()))
        }
      } catch {
        if (isActive) {
          setPatientOptions([])
        }
      } finally {
        if (isActive) {
          setPatientOptionsLoading(false)
        }
      }
    }

    void loadPatients()

    return () => {
      isActive = false
    }
  }, [debouncedPatientSearch, getPatients, selectedPatient])

  const handleSearch = useCallback(() => {
    setCurrentPage(0)
    setAppliedFilters({
      paymentDateFrom: paymentDateFrom || undefined,
      paymentDateTo: paymentDateTo || undefined,
      paymentMethods: selectedMethods.length ? selectedMethods.map((option) => option.value) : undefined,
      patientId: selectedPatient ? Number(selectedPatient.value) : undefined,
      payerId: selectedPayer ? Number(selectedPayer.value) : undefined,
      billingLocation: selectedLocation?.value,
    })
  }, [paymentDateFrom, paymentDateTo, selectedMethods, selectedPatient, selectedPayer, selectedLocation])

  const handleClear = useCallback(() => {
    setPaymentDateFrom('')
    setPaymentDateTo('')
    setSelectedMethods([])
    setSelectedLocation(null)
    setSelectedPatient(null)
    setSelectedPayer(null)
    setPatientSearch('')
    setPayerSearch('')
    setCurrentPage(0)
    setAppliedFilters({})
  }, [])

  const handleExportCsv = useCallback(async () => {
    const blob = await exportBankDepositReport(appliedFilters).unwrap()
    downloadBlob(blob, `bank-deposit-report-${moment().format('YYYY-MM-DD')}.csv`)
  }, [appliedFilters, exportBankDepositReport])

  const handleExportPdf = useCallback(async () => {
    const blob = await exportBankDepositReportPdf(appliedFilters).unwrap()
    downloadBlob(blob, `bank-deposit-report-${moment().format('YYYY-MM-DD')}.pdf`)
  }, [appliedFilters, exportBankDepositReportPdf])

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Payment Date',
        accessor: 'paymentDate',
        className: 'text-nowrap',
        Cell: ({ value }: { value?: string | null }) => formatDate(value),
      },
      {
        Header: 'Received',
        accessor: 'receivedAt',
        className: 'text-nowrap',
        Cell: ({ value }: { value?: string | null }) => formatDateTime(value),
      },
      {
        Header: 'Payment Type',
        accessor: 'source',
        className: 'text-nowrap',
        Cell: ({ value }: { value: string }) => sourceLabelMap[value] || value,
      },
      {
        Header: 'Check Date',
        accessor: 'checkDate',
        className: 'text-nowrap',
        Cell: ({ value }: { value?: string | null }) => formatDate(value),
      },
      {
        Header: 'Check/Payment No.',
        accessor: 'checkNumber',
        Cell: ({ value }: { value?: string | null }) => value || '—',
      },
      {
        Header: 'Payment Reference',
        accessor: 'paymentReference',
        Cell: ({ value }: { value?: string | null }) => (
          <span css={{ wordBreak: 'break-all' }}>{value || '—'}</span>
        ),
      },
      {
        Header: 'Method',
        accessor: 'paymentMethod',
        className: 'text-nowrap',
        Cell: ({ value }: { value: string }) => methodLabelMap[value] || value,
      },
      { Header: 'Payer', accessor: 'payerName' },
      {
        Header: 'Patient',
        accessor: 'patientLastName',
        Cell: ({ row }: { row: { original: BankDepositReportItem } }) =>
          [row.original.patientLastName, row.original.patientFirstName].filter(Boolean).join(', ') || '—',
      },
      { Header: 'Account', accessor: 'accountNumber' },
      {
        Header: 'Billing Location',
        accessor: 'billingLocation',
        Cell: ({ value }: { value?: string | null }) =>
          value ? billingLocationLabelMap[value] || value : '—',
      },
      {
        Header: 'Amount',
        accessor: 'amount',
        className: 'text-right',
        Cell: ({ value }: { value?: number | null }) => formatMoney(value),
      },
    ],
    [],
  )

  const tableData = useMemo(
    () => ({
      items: data?.transactions?.items || [],
      currentPage: data?.transactions?.currentPage ?? currentPage,
      totalItems: data?.transactions?.totalItems ?? 0,
    }),
    [currentPage, data],
  )

  const totals = data?.totals
  const hasResults = !!tableData.totalItems
  const isExporting = isExportingCsv || isExportingPdf

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <Card className="mt-3">
            <Card.Header>
              <div className="d-flex flex-wrap align-items-center justify-content-between">
                <h4 className="mb-0">Bank Deposit</h4>
                {(isFetching || isExporting) && <Spinner animation="border" size="sm" />}
              </div>
            </Card.Header>
            <Card.Body>
              <div className="row mb-3">
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Payment Date From</label>
                  <Form.Control
                    type="date"
                    value={paymentDateFrom}
                    onChange={(event) => setPaymentDateFrom(event.target.value)}
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Payment Date To</label>
                  <Form.Control
                    type="date"
                    value={paymentDateTo}
                    onChange={(event) => setPaymentDateTo(event.target.value)}
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Payment Method</label>
                  <Select
                    classNamePrefix="react-select"
                    isMulti
                    isClearable
                    value={selectedMethods}
                    onChange={(options) =>
                      setSelectedMethods([...(options as Option<BankDepositPaymentMethod>[])])
                    }
                    options={methodOptions}
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Billing Location</label>
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    value={selectedLocation}
                    onChange={(option) => setSelectedLocation(option as Option<string> | null)}
                    options={billingLocationOptions.filter(
                      ({ value }) => isIRhythmMode || value !== 'IRHYTHM',
                    )}
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Patient</label>
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    isLoading={patientOptionsLoading}
                    value={selectedPatient}
                    inputValue={patientSearch}
                    onInputChange={(value) => setPatientSearch(value)}
                    onChange={(option) => setSelectedPatient(option as Option<string> | null)}
                    options={patientOptions}
                    placeholder="Name or ID"
                    noOptionsMessage={() =>
                      patientSearch.trim() ? 'No patients found' : 'Type a name or patient ID'
                    }
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Payer</label>
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    isLoading={payersLoading}
                    value={selectedPayer}
                    inputValue={payerSearch}
                    onInputChange={(value) => setPayerSearch(value)}
                    onChange={(option) => setSelectedPayer(option as Option<string> | null)}
                    options={payerOptions}
                    formatOptionLabel={formatPayerOptionLabel}
                    filterOption={filterPayerOption}
                    placeholder="Payer name"
                    noOptionsMessage={() =>
                      payerSearch.trim().length >= 2 ? 'No payers found' : 'Type at least 2 characters'
                    }
                  />
                </div>
                <div className="col-md-6 col-sm-12 mb-2 d-flex align-items-end flex-wrap">
                  <Button variant="primary" onClick={handleSearch} disabled={isFetching}>
                    Search
                  </Button>
                  <Button variant="outline-secondary" className="ml-2" onClick={handleClear}>
                    Clear
                  </Button>
                  <Button
                    variant="outline-success"
                    className="ml-2"
                    onClick={handleExportCsv}
                    disabled={isExporting || !hasResults}>
                    Export CSV
                  </Button>
                  <Button
                    variant="outline-danger"
                    className="ml-2"
                    onClick={handleExportPdf}
                    disabled={isExporting || !hasResults}>
                    Export PDF
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <Card className="mt-3">
            <Card.Header>
              <div className="d-flex flex-wrap align-items-center justify-content-between">
                <h4 className="mb-0">Payments</h4>
                {isFetching && <Spinner animation="border" size="sm" />}
              </div>
            </Card.Header>
            <Card.Body>
              {!!totals?.byMethod?.length && (
                <div className="d-flex flex-wrap mb-3" css={{ gap: '1.5rem' }}>
                  {totals.byMethod.map((row) => (
                    <div key={row.method}>
                      <div className="textGray">{methodLabelMap[row.method] || row.method}</div>
                      <strong>{formatMoney(row.amount)}</strong>
                      <span className="text-muted ml-1">({row.transactions})</span>
                    </div>
                  ))}
                  <div>
                    <div className="textGray">Total</div>
                    <strong>{formatMoney(totals.totalAmount)}</strong>
                    <span className="text-muted ml-1">({totals.totalTransactions})</span>
                  </div>
                </div>
              )}
              <TableWrapper
                className="card-body row"
                css={{
                  '& thead': {
                    zIndex: '0 !important',
                  },
                }}>
                <DataTable
                  tableKey="bank_deposit_report"
                  data={tableData}
                  status={isFetching}
                  tableColumns={tableColumns}
                  pageSize={pageSize}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                  setPageSize={setPageSize}
                  setSortOptions={() => {}}
                  searchable={false}
                  entityName="payments"
                />
              </TableWrapper>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default BankDepositReport
